import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PLACEHOLDER_PHONE, PLACEHOLDER_EMAIL } from '../lib/contactDefaults';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  FileText,
  User,
  MapPin,
  Phone,
  Mail,
  Building2,
  PoundSterling,
  Calendar,
  ClipboardList,
  HardHat,
  Plus
} from 'lucide-react';
import { format, addDays } from 'date-fns';

const PROJECT_TYPE_OPTIONS = [
  'Extension',
  'Loft Conversion',
  'Garden Room',
  'Garage Conversion',
  'Refurb',
  'New Build',
  'Insurance',
  'Other'
];

const PLANNING_STATUS_OPTIONS = [
  'Approved',
  'In Process',
  'Exploring Options',
  'Not Required'
];

const BUDGET_RANGE_OPTIONS = [
  '£10k - £20k',
  '£20k - £50k',
  '£50k - £80k',
  '£80k - £150k',
  '£150k+'
];

const START_DATE_OPTIONS = [
  '1-2 months',
  '2-6 months',
  '6-12 months'
];

const TENDER_TYPE_OPTIONS = [
  { value: 'DEV', label: 'Development' },
  { value: 'CON', label: 'Construction' }
];

const TENDER_SOURCE_OPTIONS = [
  { value: 'Main Contractor', label: 'Main Contractor' },
  { value: 'Residential Client', label: 'Residential Client' }
];

export function CreateTender() {
  const [formData, setFormData] = useState({
    tender_type: '',
    tender_source: 'Main Contractor',  // Default to Main Contractor
    contact_id: '',  // For linking to contractor
    architect_id: '',  // For linking to architect
    estimator_id: '',  // For linking to estimator (admin user)
    client_name: '',
    address: '',
    postcode: '',
    site_address: '',
    site_postcode: '',
    telephone: PLACEHOLDER_PHONE,
    email: PLACEHOLDER_EMAIL,
    project_types: [],  // Multiple project types
    planning_status: '',
    budget_range: '',
    start_date_window: '',
    date_received: format(new Date(), 'yyyy-MM-dd'),  // Default to today
    return_date: '',  // Deadline for submitting the quote
    notes: ''
  });
  const [siteAddressSame, setSiteAddressSame] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [contractors, setContractors] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [loadingArchitects, setLoadingArchitects] = useState(false);
  const [loadingEstimators, setLoadingEstimators] = useState(false);
  const [showNewArchitectDialog, setShowNewArchitectDialog] = useState(false);
  const [newArchitect, setNewArchitect] = useState({
    name: '',
    company: '',
    telephone: PLACEHOLDER_PHONE,
    email: PLACEHOLDER_EMAIL,
    address: '',
    postcode: ''
  });

  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Fetch contractors when tender source is Main Contractor
  const fetchContractors = useCallback(async () => {
    setLoadingContractors(true);
    try {
      const response = await api.get('/contacts?category=Contractor');
      setContractors(response.data);
    } catch (error) {
      console.error('Failed to fetch contractors:', error);
    } finally {
      setLoadingContractors(false);
    }
  }, [api]);

  // Fetch architects
  const fetchArchitects = useCallback(async () => {
    setLoadingArchitects(true);
    try {
      const response = await api.get('/contacts?category=Architect');
      setArchitects(response.data);
    } catch (error) {
      console.error('Failed to fetch architects:', error);
    } finally {
      setLoadingArchitects(false);
    }
  }, [api]);

  // Fetch estimators (admin users)
  const fetchEstimators = useCallback(async () => {
    setLoadingEstimators(true);
    try {
      const response = await api.get('/users/admins');
      setEstimators(response.data);
    } catch (error) {
      console.error('Failed to fetch estimators:', error);
    } finally {
      setLoadingEstimators(false);
    }
  }, [api]);

  // Load contractors, architects, and estimators ONCE on mount.
  // Subsequent source changes are handled in handleTenderSourceChange,
  // so we intentionally omit formData.tender_source from the deps.
  useEffect(() => {
    if (formData.tender_source === 'Main Contractor') {
      fetchContractors();
    }
    fetchArchitects();
    fetchEstimators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchContractors, fetchArchitects, fetchEstimators]);

  // Redirect if not admin
  if (!isAdmin) {
    navigate('/projects');
    return null;
  }

  const handleTenderSourceChange = (value) => {
    setFormData(prev => ({ ...prev, tender_source: value, contact_id: '' }));
    if (value === 'Main Contractor') {
      fetchContractors();
    }
  };

  const handleContractorChange = (contactId) => {
    setFormData(prev => ({ ...prev, contact_id: contactId }));
    // If a contractor is selected, populate some fields from the contact
    if (contactId) {
      const contractor = contractors.find(c => c.id === contactId);
      if (contractor) {
        setFormData(prev => ({
          ...prev,
          contact_id: contactId,
          client_name: contractor.company || contractor.name,
          telephone: contractor.telephone || prev.telephone,
          email: contractor.email || prev.email
        }));
      }
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleCreateArchitect = async () => {
    if (!newArchitect.name.trim()) {
      toast.error('Please enter an architect name');
      return;
    }
    
    try {
      const response = await api.post('/contacts', {
        name: newArchitect.name.trim(),
        company: newArchitect.company.trim() || null,
        telephone: newArchitect.telephone.trim() || null,
        email: newArchitect.email.trim() || null,
        address: newArchitect.address.trim() || null,
        postcode: newArchitect.postcode.trim().toUpperCase() || null,
        category: 'Architect',
        marketing_consent: 'Awaiting'
      });
      
      toast.success('Architect added successfully');
      setShowNewArchitectDialog(false);
      setNewArchitect({ name: '', company: '', telephone: PLACEHOLDER_PHONE, email: PLACEHOLDER_EMAIL, address: '', postcode: '' });
      
      // Refresh architects list and select the new one
      await fetchArchitects();
      setFormData(prev => ({ ...prev, architect_id: response.data.id }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create architect');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.tender_type) newErrors.tender_type = 'Tender type is required';
    if (!formData.client_name.trim()) newErrors.client_name = 'Client name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.telephone.trim()) newErrors.telephone = 'Telephone is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.project_types || formData.project_types.length === 0) newErrors.project_types = 'At least one project type is required';
    if (!formData.planning_status) newErrors.planning_status = 'Planning status is required';
    if (!formData.budget_range) newErrors.budget_range = 'Budget range is required';
    if (!formData.start_date_window) newErrors.start_date_window = 'Start date window is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        site_address: siteAddressSame ? formData.address : formData.site_address,
        site_postcode: siteAddressSame ? formData.postcode : formData.site_postcode
      };

      const response = await api.post('/tenders', submitData);
      toast.success('Tender created successfully');
      navigate('/tenders');
    } catch (error) {
      console.error('Failed to create tender:', error);
      toast.error(error.response?.data?.detail || 'Failed to create tender');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleProjectTypeToggle = (type) => {
    setFormData(prev => {
      const currentTypes = prev.project_types || [];
      if (currentTypes.includes(type)) {
        return { ...prev, project_types: currentTypes.filter(t => t !== type) };
      } else {
        return { ...prev, project_types: [...currentTypes, type] };
      }
    });
    if (errors.project_types) {
      setErrors(prev => ({ ...prev, project_types: null }));
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/tenders')}
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 
          className="text-2xl md:text-3xl font-bold text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          New Tender
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tender Type Selection */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-[#F5A623]" />
              Tender Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label className="text-gray-300">Select Type *</Label>
              <Select value={formData.tender_type} onValueChange={(v) => handleChange('tender_type', v)}>
                <SelectTrigger 
                  className={`bg-[#1E2530] border-white/20 text-white ${errors.tender_type ? 'border-red-500' : ''}`}
                  data-testid="tender-type-select"
                >
                  <SelectValue placeholder="Select tender type" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {TENDER_TYPE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-white focus:bg-[#F5A623]/20 focus:text-white">
                      {option.value} - {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.tender_type && <p className="text-red-400 text-xs">{errors.tender_type}</p>}
              <p className="text-xs text-gray-500">DEV = Development | CON = Construction</p>
            </div>
            
            {/* Tender Source */}
            <div className="space-y-2 mt-4 pt-4 border-t border-white/10">
              <Label className="text-gray-300">Tender Source *</Label>
              <Select value={formData.tender_source} onValueChange={handleTenderSourceChange}>
                <SelectTrigger 
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="tender-source-select"
                >
                  <SelectValue placeholder="Select tender source" />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10">
                  {TENDER_SOURCE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-white focus:bg-[#F5A623]/20 focus:text-white">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {formData.tender_source === 'Main Contractor' 
                  ? 'Select or create a contractor contact to link this tender' 
                  : 'A client contact will be auto-created from the details below'}
              </p>
            </div>
            
            {/* Contractor Selection (only for Main Contractor) */}
            {formData.tender_source === 'Main Contractor' && (
              <div className="space-y-2 mt-4">
                <Label className="text-gray-300">Select Contractor</Label>
                <Select 
                  value={formData.contact_id || 'none'} 
                  onValueChange={(v) => handleContractorChange(v === 'none' ? '' : v)}
                >
                  <SelectTrigger 
                    className="bg-[#1E2530] border-white/20 text-white"
                    data-testid="contractor-select"
                  >
                    <SelectValue placeholder={loadingContractors ? "Loading contractors..." : "Select a contractor (optional)"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                    <SelectItem value="none" className="text-gray-400 focus:bg-[#F5A623]/20 focus:text-white">
                      No contractor selected
                    </SelectItem>
                    {contractors.map(contractor => (
                      <SelectItem 
                        key={contractor.id} 
                        value={contractor.id} 
                        className="text-white focus:bg-[#F5A623]/20 focus:text-white"
                      >
                        {contractor.company || contractor.name}
                        {contractor.company && contractor.name !== contractor.company && (
                          <span className="text-gray-400 ml-2">({contractor.name})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Can't find the contractor? Add them in the <a href="/contacts" className="text-[#F5A623] hover:underline">Contacts</a> section first.
                </p>
              </div>
            )}
            
            {/* Architect Selection (always visible) */}
            <div className="space-y-2 mt-4 pt-4 border-white/10">
              <Label className="text-gray-300">Architect</Label>
              <Select 
                value={formData.architect_id || 'na'} 
                onValueChange={(v) => {
                  if (v === 'add_new') {
                    // Use setTimeout to let the Select close first before opening Dialog
                    setTimeout(() => setShowNewArchitectDialog(true), 100);
                  } else {
                    setFormData(prev => ({ ...prev, architect_id: v === 'na' ? '' : v }));
                  }
                }}
              >
                <SelectTrigger 
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="architect-select"
                >
                  <SelectValue placeholder={loadingArchitects ? "Loading architects..." : "Select an architect"} />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                  <SelectItem value="na" className="text-gray-400 focus:bg-[#F5A623]/20 focus:text-white">
                    N/A - No architect
                  </SelectItem>
                  <SelectItem value="add_new" className="text-[#F5A623] focus:bg-[#F5A623]/20 focus:text-white font-medium">
                    + Add New Architect
                  </SelectItem>
                  {architects.map(architect => (
                    <SelectItem 
                      key={architect.id} 
                      value={architect.id} 
                      className="text-white focus:bg-[#F5A623]/20 focus:text-white"
                    >
                      {architect.company || architect.name}
                      {architect.company && architect.name !== architect.company && (
                        <span className="text-gray-400 ml-2">({architect.name})</span>
                      )}
                      {architect.success_rate !== null && (
                        <span className={`ml-2 text-xs ${
                          architect.success_rate >= 50 ? 'text-green-400' : 
                          architect.success_rate >= 25 ? 'text-amber-400' : 
                          'text-gray-400'
                        }`}>
                          ({architect.success_rate}% success)
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Optional: Link an architect to this tender for tracking success rates.
              </p>
            </div>

            {/* Estimator Selection */}
            <div className="space-y-2 mt-4 pt-4 border-t border-white/10">
              <Label className="text-gray-300">Estimator</Label>
              <Select 
                value={formData.estimator_id || 'not_assigned'} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, estimator_id: v === 'not_assigned' ? '' : v }))}
              >
                <SelectTrigger 
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="estimator-select"
                >
                  <SelectValue placeholder={loadingEstimators ? "Loading estimators..." : "Select an estimator"} />
                </SelectTrigger>
                <SelectContent className="bg-[#2A3240] border-white/10 max-h-60">
                  <SelectItem value="not_assigned" className="text-gray-400 focus:bg-[#F5A623]/20 focus:text-white">
                    Not Assigned
                  </SelectItem>
                  {estimators.map(estimator => (
                    <SelectItem 
                      key={estimator.id} 
                      value={estimator.id} 
                      className="text-white focus:bg-[#F5A623]/20 focus:text-white"
                    >
                      {estimator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Optional: Assign an estimator to this tender.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Client & Contact Information */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#F5A623]" />
              {formData.tender_source === 'Main Contractor' ? 'Project Contact Information' : 'Client Information'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">
                {formData.tender_source === 'Main Contractor' ? 'Project/Client Name *' : 'Client Name *'}
              </Label>
              <Input
                value={formData.client_name}
                onChange={(e) => handleChange('client_name', e.target.value)}
                placeholder={formData.tender_source === 'Main Contractor' ? "Project name or end client" : "John Smith"}
                data-testid="client-name-input"
                className={`bg-[#1E2530] border-white/20 text-white ${errors.client_name ? 'border-red-500' : ''}`}
              />
              {errors.client_name && <p className="text-red-400 text-xs">{errors.client_name}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Telephone *
                </Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => handleChange('telephone', e.target.value)}
                  placeholder="07700 900000"
                  data-testid="telephone-input"
                  className={`bg-[#1E2530] border-white/20 text-white ${errors.telephone ? 'border-red-500' : ''}`}
                />
                {errors.telephone && <p className="text-red-400 text-xs">{errors.telephone}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="client@example.com"
                  data-testid="email-input"
                  className={`bg-[#1E2530] border-white/20 text-white ${errors.email ? 'border-red-500' : ''}`}
                />
                {errors.email && <p className="text-red-400 text-xs">{errors.email}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-[#F5A623]" />
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Client Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street"
                data-testid="address-input"
                className={`bg-[#1E2530] border-white/20 text-white ${errors.address ? 'border-red-500' : ''}`}
              />
              {errors.address && <p className="text-red-400 text-xs">{errors.address}</p>}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Postcode *</Label>
              <Input
                value={formData.postcode}
                onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())}
                placeholder="NN1 1AA"
                data-testid="postcode-input"
                className={`bg-[#1E2530] border-white/20 text-white uppercase ${errors.postcode ? 'border-red-500' : ''}`}
              />
              {errors.postcode && <p className="text-red-400 text-xs">{errors.postcode}</p>}
            </div>

            <div className="flex items-center space-x-2 py-2">
              <Checkbox 
                id="site-same" 
                checked={siteAddressSame} 
                onCheckedChange={setSiteAddressSame}
                className="border-white/30 data-[state=checked]:bg-[#F5A623] data-[state=checked]:border-[#F5A623]"
              />
              <Label htmlFor="site-same" className="text-gray-300 cursor-pointer">
                Site address same as client address
              </Label>
            </div>

            {!siteAddressSame && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-300">Site Address</Label>
                  <Input
                    value={formData.site_address}
                    onChange={(e) => handleChange('site_address', e.target.value)}
                    placeholder="456 Build Site Road"
                    data-testid="site-address-input"
                    className="bg-[#1E2530] border-white/20 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Site Postcode</Label>
                  <Input
                    value={formData.site_postcode}
                    onChange={(e) => handleChange('site_postcode', e.target.value.toUpperCase())}
                    placeholder="NN2 2BB"
                    data-testid="site-postcode-input"
                    className="bg-[#1E2530] border-white/20 text-white uppercase"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Project Details */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-[#F5A623]" />
              Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Project Types - Multi-select checkboxes */}
            <div className="space-y-2">
              <Label className="text-gray-300">Project Type(s) *</Label>
              <div className={`grid grid-cols-2 md:grid-cols-3 gap-2 p-3 rounded-lg bg-[#1E2530] border ${errors.project_types ? 'border-red-500' : 'border-white/20'}`}>
                {PROJECT_TYPE_OPTIONS.map(type => (
                  <label 
                    key={type} 
                    className="flex items-center gap-2 cursor-pointer hover:bg-white/5 p-2 rounded transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={formData.project_types?.includes(type) || false}
                      onChange={() => handleProjectTypeToggle(type)}
                      className="w-4 h-4 rounded border-white/20 bg-[#2A3240] text-[#F5A623] focus:ring-[#F5A623] focus:ring-offset-0"
                    />
                    <span className="text-white text-sm">{type}</span>
                  </label>
                ))}
              </div>
              {errors.project_types && <p className="text-red-400 text-xs">{errors.project_types}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Planning Status *</Label>
                <Select value={formData.planning_status} onValueChange={(v) => handleChange('planning_status', v)}>
                  <SelectTrigger 
                    className={`bg-[#1E2530] border-white/20 text-white ${errors.planning_status ? 'border-red-500' : ''}`}
                    data-testid="planning-status-select"
                  >
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {PLANNING_STATUS_OPTIONS.map(status => (
                      <SelectItem key={status} value={status} className="text-white focus:bg-[#F5A623]/20">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.planning_status && <p className="text-red-400 text-xs">{errors.planning_status}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date Received
                </Label>
                <Input
                  type="date"
                  value={formData.date_received}
                  onChange={(e) => handleChange('date_received', e.target.value)}
                  data-testid="date-received-input"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Return Date
                </Label>
                <Input
                  type="date"
                  value={formData.return_date}
                  onChange={(e) => handleChange('return_date', e.target.value)}
                  data-testid="return-date-input"
                  className="bg-[#1E2530] border-white/20 text-white"
                />
                <p className="text-gray-500 text-xs">Deadline for submitting the quote</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Budget Range *</Label>
                <Select value={formData.budget_range} onValueChange={(v) => handleChange('budget_range', v)}>
                  <SelectTrigger 
                    className={`bg-[#1E2530] border-white/20 text-white ${errors.budget_range ? 'border-red-500' : ''}`}
                    data-testid="budget-range-select"
                  >
                    <SelectValue placeholder="Select budget" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {BUDGET_RANGE_OPTIONS.map(budget => (
                      <SelectItem key={budget} value={budget} className="text-white focus:bg-[#F5A623]/20">
                        {budget}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.budget_range && <p className="text-red-400 text-xs">{errors.budget_range}</p>}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Planned Start Window *</Label>
                <Select value={formData.start_date_window} onValueChange={(v) => handleChange('start_date_window', v)}>
                  <SelectTrigger 
                    className={`bg-[#1E2530] border-white/20 text-white ${errors.start_date_window ? 'border-red-500' : ''}`}
                    data-testid="start-date-window-select"
                  >
                    <SelectValue placeholder="Select window" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2A3240] border-white/10">
                    {START_DATE_OPTIONS.map(window => (
                      <SelectItem key={window} value={window} className="text-white focus:bg-[#F5A623]/20">
                        {window}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.start_date_window && <p className="text-red-400 text-xs">{errors.start_date_window}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-[#2A3240] border-white/10">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <ClipboardList className="w-5 h-5 text-[#F5A623]" />
              Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Add any internal notes about this tender..."
              rows={4}
              data-testid="notes-input"
              className="bg-[#1E2530] border-white/20 text-white resize-none"
            />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/tenders')}
            className="flex-1 border-white/20 text-gray-300 hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="submit-tender-btn"
            className="flex-1 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Create Tender
              </>
            )}
          </Button>
        </div>
      </form>

      {/* New Architect Dialog */}
      <Dialog open={showNewArchitectDialog} onOpenChange={setShowNewArchitectDialog}>
        <DialogContent 
          className="bg-[#2A3240] border-white/10 text-white max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#F5A623]" />
              Add New Architect
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Create a new architect contact to link to this tender.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Name *</Label>
              <Input
                value={newArchitect.name}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-name"
                autoFocus
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Company</Label>
              <Input
                value={newArchitect.company}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Architecture firm name"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-company"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Telephone</Label>
                <Input
                  value={newArchitect.telephone}
                  onChange={(e) => setNewArchitect(prev => ({ ...prev, telephone: e.target.value }))}
                  placeholder="Phone number"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="new-architect-telephone"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={newArchitect.email}
                  onChange={(e) => setNewArchitect(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                  className="bg-[#1E2530] border-white/20 text-white"
                  data-testid="new-architect-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Address</Label>
              <Input
                value={newArchitect.address}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Street address"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-address"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Postcode</Label>
              <Input
                value={newArchitect.postcode}
                onChange={(e) => setNewArchitect(prev => ({ ...prev, postcode: e.target.value }))}
                placeholder="NN1 1AA"
                className="bg-[#1E2530] border-white/20 text-white"
                data-testid="new-architect-postcode"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowNewArchitectDialog(false);
                setNewArchitect({ name: '', company: '', telephone: PLACEHOLDER_PHONE, email: PLACEHOLDER_EMAIL, address: '', postcode: '' });
              }}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateArchitect}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="create-architect-submit"
            >
              Add Architect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
